import { ChangeDetectionStrategy, Component, computed, inject, Injector, ViewEncapsulation } from "@angular/core";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25CheckboxModule } from "../../s25-checkbox/s25.checkbox.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25ModalModule } from "../../s25-modal/s25.modal.module";
import { SeEditableLogo } from "../se.editable.logo.component";
import { Item } from "../../../pojo/Item";
import { SEEventDetailsStore } from "./se.event.details.store";
import { SeAttendanceStore } from "../attendance/se.attendance.store";
import { SeOrgSecurityStore } from "../se.org.security.store";
import { Bind } from "../../../decorators/bind.decorator";
import {
    attendanceDisplayName,
    AttendanceState,
    attendanceState,
    UpdateAttendanceOptions,
} from "../attendance/attendance.service";
import { LayoutWidget, LeafSettingIds } from "../layouts/se.layout.widget.abstract";
import { Layouts } from "../layouts/se.layouts";
import { IngestionFormComponent } from "../../data-ingestion/ingestion.form.component";
import { IngestionFieldAnswer } from "../../data-ingestion/data.ingestion.service";
import { S25Icons } from "../../s25-icon/s25.icon.const";
import { INGESTION_SYSTEM_FIELDS } from "../../data-ingestion/data.ingestion.const";
import { S25ImageWithPlaceholderComponent } from "../../../standalone/s25.image.with.placeholder.component";
import { S25RouterLinkDirective } from "../../s25-nav-bar/s25.router.link.directive";
import { S25States } from "../../../services/state/state.map";
import { NgTemplateOutlet } from "@angular/common";
import { TypedTemplateContextDirective } from "../../../standalone/typed.template.directive";
import { Merge } from "../../../pojo/Util";
import { LayoutsConfig } from "../layouts/se.layouts.config";
import { S25Util } from "../../../util/s25-util";
import { S25PipesModule } from "../../../pipes/s25.pipes.module";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { ModalService } from "../../modal/modal.service";
import { LoginStore } from "../../../stores/login.store";
import { NotificationService } from "../../../services/notification.service";
import { S25Const } from "../../../util/s25-const";
@Component({
    standalone: true,
    selector: "s25-ng-se-event-details-widget",
    template: `
        <div class="event-header">
            @if (settingValues()["eventImage-visible"] === "true") {
                <se-editable-logo
                    [itemId]="eventStore.eventId()"
                    [itemTypeId]="Item.Ids.Event"
                    [imageAttr]="eventStore.imageAttr()"
                    [canEdit]="canEdit()"
                ></se-editable-logo>
            }

            <div class="event-info">
                <ng-template #icon [templateContext]="IconContext" let-setting="setting">
                    @let icon = settingValues()[setting];
                    @if (!!icon) {
                        <s25-ng-icon [type]="asIconType(icon)" [size]="20" class="detail-icon" [ariaLabel]="false" />
                    }
                </ng-template>

                <div class="event-details-grid">
                    @for (field of fields(); track field.id) {
                        <div class="detail-item">
                            @switch (field.id) {
                                @case ("name") {
                                    <span class="detail-label">{{ settingValues()["name-label"] }}</span>
                                    <div class="detail-content" [style]="settingValues()['name-css']">
                                        <ng-container *ngTemplateOutlet="icon; context: { setting: 'name-icon' }" />
                                        <span class="detail-value">{{ eventStore.name() }}</span>
                                    </div>
                                }
                                @case ("title") {
                                    <span class="detail-label">{{ settingValues()["title-label"] }}</span>
                                    <div class="detail-content" [style]="settingValues()['title-css']">
                                        <ng-container *ngTemplateOutlet="icon; context: { setting: 'title-icon' }" />
                                        <span class="detail-value">{{ eventStore.title() }}</span>
                                    </div>
                                }
                                @case ("locator") {
                                    <span class="detail-label">{{ settingValues()["locator-label"] }}</span>
                                    <div class="detail-content" [style]="settingValues()['locator-css']">
                                        <ng-container *ngTemplateOutlet="icon; context: { setting: 'locator-icon' }" />
                                        <span class="detail-value">{{ eventStore.locator() }}</span>
                                    </div>
                                }
                                @case ("dateTime") {
                                    <span class="detail-label">{{ settingValues()["dateTime-label"] }}</span>
                                    <div class="detail-content" [style]="settingValues()['dateTime-css']">
                                        <ng-container *ngTemplateOutlet="icon; context: { setting: 'dateTime-icon' }" />
                                        <span class="detail-value">{{ eventStore.date() }}</span>
                                    </div>
                                }
                                @case ("locations") {
                                    <span class="detail-label">{{ settingValues()["locations-label"] }}</span>
                                    <div class="detail-content detail-list">
                                        @for (location of eventStore.locations(); track location.id) {
                                            <div class="location icon-row">
                                                <ng-container
                                                    *ngTemplateOutlet="icon; context: { setting: 'locations-icon' }"
                                                />
                                                <span class="detail-value">{{ location.name }}</span>
                                            </div>
                                        }
                                    </div>
                                }
                                @case ("resources") {
                                    <span class="detail-label">{{ resources().label }}</span>
                                    <div class="detail-content detail-list">
                                        @for (resource of eventStore.resources(); track resource.id) {
                                            <div class="resource icon-row">
                                                <ng-container
                                                    *ngTemplateOutlet="icon; context: { setting: 'resources-icon' }"
                                                />
                                                <span class="detail-value">{{ resource.name }}</span>
                                            </div>
                                        }
                                    </div>
                                }
                                @case ("categories") {
                                    <span class="detail-label">{{ categories().label }}</span>
                                    <div class="detail-content detail-list">
                                        @for (category of eventStore.categories(); track category.id) {
                                            <div class="categories icon-row">
                                                <ng-container
                                                    *ngTemplateOutlet="icon; context: { setting: 'categories-icon' }"
                                                />
                                                <span class="detail-value">{{ category.name }}</span>
                                            </div>
                                        }
                                    </div>
                                }
                                @case ("registration") {
                                    <span class="detail-label">{{ settingValues()["registration-label"] }}</span>
                                    @if (loginStore.loggedIn() && eventStore.followState() !== "FOLLOWING") {
                                        <p class="attendance-state">
                                            {{
                                                eventStore.attendanceState().mixed
                                                    ? "Attendance varies based on the occurrence"
                                                    : eventStore.attendanceState().state
                                            }}
                                        </p>
                                    }
                                    <div class="detail-content buttons">
                                        @if (loginStore.loggedIn() && eventStore.canUnregister()?.length) {
                                            <s25-ng-button [type]="'primary'" [onClick]="onUnregisterClick">
                                                Unregister
                                            </s25-ng-button>
                                        }
                                        @if (eventStore.attendanceState().unregistered.length) {
                                            <s25-ng-button
                                                class="register-button"
                                                [type]="'primary'"
                                                [onClick]="onRegisterClick"
                                                [disabled]="eventStore.showRegistrationForm()"
                                            >
                                                Register
                                            </s25-ng-button>
                                        }
                                    </div>
                                }
                                @case ("occurrenceNotes") {
                                    <span class="detail-label">{{ occurrenceNotes().label }}</span>
                                    <div class="detail-content detail-list">
                                        @for (occurrence of eventStore.occurrences(); track occurrence.reservationId) {
                                            @if (occurrence.notes) {
                                                <div class="occurrence-note">
                                                    <ng-container
                                                        [ngTemplateOutlet]="icon"
                                                        [ngTemplateOutletContext]="{ setting: 'occurrenceNotes-icon' }"
                                                    />
                                                    <span class="detail-value">{{ occurrence.notes }}</span>
                                                </div>
                                            }
                                        }
                                    </div>
                                }
                                @case ("hostOrg") {
                                    <span class="detail-label">{{ hostOrg().label }}</span>
                                    <div class="detail-content">
                                        <a
                                            class="host-org"
                                            s25-ng-router-link
                                            [stateName]="S25States.names.seOrganizationDetails"
                                            [stateParams]="{ itemId: eventStore.hostOrg()!.itemId }"
                                        >
                                            <ng-template #orgLogoPlaceholder>
                                                <div class="org-image-placeholder">
                                                    <s25-ng-icon
                                                        [type]="'organization'"
                                                        [size]="80"
                                                        [ariaLabel]="false"
                                                    />
                                                </div>
                                            </ng-template>
                                            <s25-ng-image-with-placeholder
                                                class="org-logo"
                                                [url]="eventStore.hostOrg()!.logoUrl"
                                                [altText]="eventStore.hostOrg()!.itemName + ' logo'"
                                                [placeholder]="orgLogoPlaceholder"
                                            />
                                            <span class="detail-value">{{ eventStore.hostOrg()!.itemName }}</span>
                                        </a>
                                    </div>
                                }
                                @case ("additionalOrgs") {
                                    <span class="detail-label">{{ additionalOrgs().label }}</span>
                                    <div class="detail-content detail-list">
                                        @for (org of eventStore.additionalOrgs(); track org.id) {
                                            <a
                                                class="detail-value"
                                                s25-ng-router-link
                                                [stateName]="S25States.names.seOrganizationDetails"
                                                [stateParams]="{ itemId: org.id }"
                                            >
                                                {{ org.name }}
                                            </a>
                                        }
                                    </div>
                                }
                                @case ("requirements") {
                                    <span class="detail-label">{{ settingValues()["requirements-label"] }}</span>
                                    <div class="detail-content detail-list">
                                        @for (item of eventStore.requirements(); track item.id) {
                                            <div class="requirements icon-row">
                                                <ng-container
                                                    *ngTemplateOutlet="icon; context: { setting: 'requirements-icon' }"
                                                />
                                                <div class="detail-value">
                                                    <p>
                                                        @if (item.quantity) {
                                                            <span class="ngBold">{{ item.quantity }}</span>
                                                        }
                                                        {{ item.name }}
                                                    </p>
                                                    @if (item.comments) {
                                                        <div class="hint">{{ item.comments }}</div>
                                                    }
                                                </div>
                                            </div>
                                        }
                                    </div>
                                }
                                @case ("confirmationText") {
                                    <span class="detail-label">{{ settingValues()["confirmationText-label"] }}</span>
                                    <div class="detail-content" [style]="settingValues()['confirmationText-css']">
                                        <ng-container
                                            *ngTemplateOutlet="icon; context: { setting: 'confirmationText-icon' }"
                                        />
                                        <span class="detail-value">{{ eventStore.confirmationText() }}</span>
                                    </div>
                                }
                                @case ("description") {
                                    <span class="detail-label">{{ settingValues()["description-label"] }}</span>
                                    <div class="detail-content">
                                        <ng-container
                                            *ngTemplateOutlet="icon; context: { setting: 'description-icon' }"
                                        />
                                        <div
                                            class="detail-value"
                                            [innerHTML]="eventStore.description()! | safeHTML"
                                        ></div>
                                    </div>
                                }
                                @case ("internalNotes") {
                                    <span class="detail-label">{{ settingValues()["internalNotes-label"] }}</span>
                                    <div class="detail-content" [style]="settingValues()['internalNotes-css']">
                                        <ng-container
                                            *ngTemplateOutlet="icon; context: { setting: 'internalNotes-icon' }"
                                        />
                                        <span class="detail-value">{{ eventStore.internalNotes() }}</span>
                                    </div>
                                }
                                @case ("comments") {
                                    <span class="detail-label">{{ settingValues()["comments-label"] }}</span>
                                    <div class="detail-content detail-list">
                                        @for (item of eventStore.comments(); track item) {
                                            <div class="comments">{{ item }}</div>
                                        }
                                    </div>
                                }
                                @case ("attendeeCounts") {
                                    <span class="detail-label">{{ attendeeCounts().label }}</span>
                                    <div class="detail-content detail-list attendee-counts">
                                        @for (item of attendeeCounts().counts; track item) {
                                            <div class="state">{{ item.name }}</div>
                                            <div class="count">{{ item.count }}</div>
                                        }
                                    </div>
                                }
                                @case ("eventType") {
                                    <span class="detail-label">{{ settingValues()["eventType-label"] }}</span>
                                    <div class="detail-content" [style]="settingValues()['eventType-css']">
                                        <ng-container
                                            *ngTemplateOutlet="icon; context: { setting: 'eventType-icon' }"
                                        />
                                        <span class="detail-value">{{ eventStore.eventType()!.name }}</span>
                                    </div>
                                }
                                @case ("eventState") {
                                    <span class="detail-label">{{ settingValues()["eventState-label"] }}</span>
                                    <div class="detail-content" [style]="settingValues()['eventState-css']">
                                        <ng-container
                                            *ngTemplateOutlet="icon; context: { setting: 'eventState-icon' }"
                                        />
                                        <span class="detail-value">{{ eventStore.eventState()!.name }}</span>
                                    </div>
                                }
                            }
                        </div>
                    }
                </div>
            </div>

            @if (settingValues()["followButton-visible"] === "true" && loginStore.loggedIn()) {
                @if (eventStore.followState() === "NOT_FOLLOWING") {
                    <s25-ng-icon-button
                        class="bookmark"
                        [type]="'bookmarkOutline'"
                        (clicked)="setAtLeastInterested()"
                        [ariaLabel]="'Follow'"
                    />
                }
                @if (eventStore.followState() === "PARTIAL") {
                    <s25-ng-icon-button
                        class="bookmark"
                        [type]="'bookmarkHalfFilled'"
                        (clicked)="setAtLeastInterested()"
                        [ariaLabel]="'Follow'"
                    />
                }
                @if (eventStore.followState() === "FOLLOWING" || eventStore.followState() === "MIXED") {
                    <s25-ng-icon-button
                        class="bookmark"
                        [type]="'bookmarkFilled'"
                        [disabled]="eventStore.followState() === 'MIXED'"
                        [disabledReason]="'Cannot unfollow an event with registrations'"
                        [ariaLabel]="'Unfollow'"
                        (clicked)="setNotInterested()"
                    />
                }
            }
        </div>

        @if (eventStore.showRegistrationForm() && !!attendanceStore.registrationFormId()) {
            <div class="event-section">
                <h2>Register</h2>
                <s25-ng-ingestion-form
                    [ingestionPointId]="attendanceStore.registrationFormId()"
                    [eventId]="eventStore.eventId()"
                    [hiddenOccurrences]="eventStore.registeredOccurrences()"
                    (onSubmit)="onRegistered($event)"
                />
            </div>
        }
    `,
    styles: `
        /* Event Header Section */
        .event-header {
            display: flex;
            gap: 3rem;
            align-items: flex-start;
        }

        .event-info {
            flex: 1;
            min-width: 0;
        }

        .event-details-grid {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
        }

        .detail-icon {
            color: var(--cn-blue);
        }

        .detail-content.buttons {
            display: flex;
            align-items: center;
            gap: 0.25rem;
        }

        .detail-label {
            font-size: 0.875rem;
            color: var(--color--text-muted);
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        s25-ng-attendance-state {
            min-width: 8em;
        }

        .grid {
            display: grid;
        }

        .host-org {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        .org-logo {
            height: 2.5rem;
            aspect-ratio: 1;
            display: inline-flex;
            justify-content: center;
            border-radius: 50%;
            overflow: hidden;
        }

        .org-logo:has(.org-image-placeholder) {
            height: 2rem;
        }

        .org-image-placeholder {
            align-self: center;
        }

        .detail-item {
            min-width: 0;
        }

        .detail-content {
            min-width: 0;
        }

        .detail-value {
            overflow-wrap: break-word;
            word-break: break-word;
        }

        .detail-list {
            display: grid;
            row-gap: 0.2rem;
        }

        .icon-row {
            display: flex;
            gap: 0.25rem;
            align-items: center;
            min-width: 0;
        }

        .hint {
            color: var(--color--text-muted);
            font-size: 0.9em;
        }

        .attendee-counts {
            display: grid;
            grid-template-columns: auto 1fr;
            column-gap: 0.5rem;
        }

        .bookmark {
            font-size: 1.5em;
        }

        .bookmark ::ng-deep .c-iconButton {
            background: color-mix(in srgb, var(--color--surface-bg) 85%, transparent);
            border-radius: 50%;
            padding: 0.55em;
            line-height: 0;
            --icon-color: var(--cn-blue);
            color: var(--cn-blue);
            transition: background var(--transition-short, 80ms) ease;
        }

        .bookmark ::ng-deep .c-iconButton:hover {
            background: color-mix(in srgb, var(--color--surface-bg) 92%, transparent);
        }

        .attendance-state {
            padding-block: 0.25rem;
        }

        :host ::ng-deep .register-button .aw-button {
            background-color: var(--color--registration) !important;
        }

        .event-section {
            margin-top: 2rem;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
            :host {
                padding: 1rem;
            }

            .event-header {
                flex-direction: column;
                gap: 1.5rem;
            }

            .event-info {
                width: 100%;
            }

            .event-details-grid {
                gap: 1rem;
            }

            .detail-content.buttons {
                flex-wrap: wrap;
                gap: 0.5rem;
            }

            .bookmark {
                align-self: flex-start;
            }

            .event-section {
                margin-top: 1.5rem;
            }
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25ButtonComponent,
        S25CheckboxModule,
        S25IconComponent,
        S25ModalModule,
        SeEditableLogo,
        IngestionFormComponent,
        S25ImageWithPlaceholderComponent,
        S25RouterLinkDirective,
        NgTemplateOutlet,
        TypedTemplateContextDirective,
        S25PipesModule,
        S25IconButtonComponent,
    ],
})
export class SeEventDetailsWidgetComponent extends LayoutWidget<typeof SeEventDetailsWidgetComponent> {
    static readonly META = {
        name: "Event Details",
        info: "Display event details with customizable fields and settings",
        settings: [
            { id: "eventImage-visible", label: "Show Event Image", type: "field", fieldType: "checkbox" },
            { id: "followButton-visible", label: "Show Follow Button", type: "field", fieldType: "checkbox" },
            {
                id: "fields",
                label: "Fields",
                type: "sortable-collapses",
                settings: [
                    defaultFields("name", "Name", undefined, {}),
                    defaultFields("title", "Title", undefined, {}),
                    defaultFields("eventType", "Event Type", undefined, {}),
                    defaultFields("eventState", "State", undefined, {}),
                    defaultFields("confirmationText", "Confirmation Text", undefined, {}),
                    defaultFields("description", "Description", undefined, { css: false }),
                    defaultFields("internalNotes", "Internal Notes", undefined, {}),
                    defaultFields("comments", "Comments", undefined, { css: false }),
                    defaultFields("locator", "Locator", `Displays the event locator YYYY-ABCDEF`, {}),
                    defaultFields("dateTime", "Date & Time", undefined, {}),
                    defaultFields("locations", "Locations", undefined, { css: false }),
                    defaultFields("resources", "Resources", undefined, { css: false }),
                    defaultFields("categories", "Categories", undefined, { css: false }),
                    defaultFields("requirements", "Requirements", undefined, { css: false }),
                    defaultFields("hostOrg", "Hosted By", undefined, { icon: false, css: false }),
                    defaultFields("additionalOrgs", "Additional Organizations", undefined, {
                        icon: false,
                        css: false,
                    }),
                    {
                        id: "occurrenceNotes",
                        type: "fields",
                        label: "Occurrence Notes",
                        info: `This field displays the notes on occurrences for the event if there are any`,
                        settings: [
                            { id: "occurrenceNotes-visible", label: "Visible", type: "field", fieldType: "checkbox" },
                            { id: "occurrenceNotes-icon", label: "Icon", type: "field", fieldType: "icon" },
                            { id: "occurrenceNotes-label", label: "Label", type: "field", fieldType: "text" },
                            {
                                id: "occurrenceNotes-hideIfMulti",
                                label: "Hide if more than one occurrence",
                                type: "field",
                                fieldType: "checkbox",
                            },
                        ],
                    },
                    {
                        id: "attendeeCounts",
                        type: "fields",
                        label: "Attendee Counts",
                        settings: [
                            { id: "attendeeCounts-visible", label: "Visible", type: "field", fieldType: "checkbox" },
                            { id: "attendeeCounts-label", label: "Label", type: "field", fieldType: "text" },
                            {
                                id: "attendeeCounts-states",
                                label: "Show States",
                                type: "field",
                                fieldType: "dropdown-multiselect",
                                options: {
                                    dropdownOptions: S25Util.entries(attendanceState).map(([_, id]) => ({
                                        itemId: String(id),
                                        itemName: attendanceDisplayName[id],
                                    })),
                                },
                            },
                        ],
                    },
                    defaultFields(
                        "registration",
                        "Attendance",
                        `This field displays the user's attendance status and buttons to register or cancel registration if it has been configured for the event.`,
                        { css: false, icon: false },
                    ),
                ],
            },
        ],
        defaultSettings: {
            "dateTime-icon": "calendarDate",
            "dateTime-label": "Date & Time",
            "locations-icon": "locationMarker",
            "locations-label": "Locations",
            "resources-label": "Resources",
            "registration-label": "Attendance",
            "occurrenceNotes-label": "Notes",
            "occurrenceNotes-hideIfMulti": "true",
            "dateTime-visible": "true",
            "locations-visible": "true",
            "registration-visible": "true",
            "occurrenceNotes-visible": "true",
            "hostOrg-visible": "true",
            "hostOrg-label": "Hosted By",
            "additionalOrgs-label": "Additional Organizations",
            "categories-label": "Categories",
            "locator-label": "Locator",
            "title-label": "Title",
            "name-visible": "true",
            "name-css": "font-size: 1.4em; font-weight: bold;",
            "requirements-label": "Requirements",
            "confirmationText-label": "Confirmation Text",
            "description-label": "Description",
            "internalNotes-label": "Internal Notes",
            "comments-label": "Comments",
            "attendeeCounts-label": "Attendee Counts",
            "attendeeCounts-states": "1,2,4",
            "eventType-label": "Event Type",
            "eventState-label": "State",
            "eventImage-visible": "true",
            "followButton-visible": "true",
            "description-visible": "true",
            "additionalOrgs-visible": "true",
        },
    } as const satisfies Layouts.WidgetStatic;

    protected readonly eventStore = inject(SEEventDetailsStore);
    protected readonly orgSecStore = inject(SeOrgSecurityStore);
    protected readonly attendanceStore = inject(SeAttendanceStore);
    protected readonly loginStore = inject(LoginStore);
    protected readonly injector = inject(Injector);
    protected readonly ModalService = ModalService;

    readonly canEdit = computed(() => this.orgSecStore.canCreateEvent());

    protected readonly Item = Item;
    protected readonly S25States = S25States;
    protected readonly IconContext: { setting: LeafSettingIds<(typeof SeEventDetailsWidgetComponent)["META"]> };

    readonly fields = computed(() => {
        return this.settings()
            .find((setting) => setting.id === "fields")!
            .settings.filter((setting) => {
                if (this.settingValues()[`${setting.id}-visible`] !== "true") return false;
                switch (setting.id) {
                    case "locations":
                        return !!this.eventStore.locations()?.length;
                    case "registration":
                        return !!this.attendanceStore.canRegister();
                    case "occurrenceNotes": {
                        const hideIfMulti = this.occurrenceNotes().hideIfMulti === "true";
                        if ((this.eventStore.occurrences()?.length ?? 0) > 1 && hideIfMulti) return false;
                        const hasNotes = this.eventStore.occurrences()?.some((occ) => !!occ.notes);
                        return !!hasNotes;
                    }
                    case "hostOrg":
                        return !!this.eventStore.hostOrg();
                    case "additionalOrgs":
                        return !!this.eventStore.additionalOrgs()?.length;
                    case "resources":
                        return !!this.eventStore.resources()?.length;
                    case "categories":
                        return !!this.eventStore.categories()?.length;
                    case "title":
                        return !!this.eventStore.title();
                    case "requirements":
                        return !!this.eventStore.requirements()?.length;
                    case "confirmationText":
                        return !!this.eventStore.confirmationText();
                    case "description":
                        return !!this.eventStore.description();
                    case "internalNotes":
                        return !!this.eventStore.internalNotes();
                    case "comments":
                        return !!this.eventStore.comments()?.length;
                    case "eventType":
                        return !!this.eventStore.eventType();
                    case "eventState":
                        return !!this.eventStore.eventState();
                    default:
                        return true;
                }
            });
    });
    readonly occurrenceNotes = computed(() => this.getPrefixedSettings("occurrenceNotes-" as const));
    readonly hostOrg = computed(() => this.getPrefixedSettings("hostOrg-" as const));
    readonly additionalOrgs = computed(() => this.getPrefixedSettings("additionalOrgs-" as const));
    readonly resources = computed(() => this.getPrefixedSettings("resources-" as const));
    readonly categories = computed(() => this.getPrefixedSettings("categories-" as const));
    readonly attendeeCounts = computed(() => {
        const settings = this.getPrefixedSettings("attendeeCounts-" as const);
        const states = settings.states?.split(",").map(Number).filter(S25Util.isNumeric) ?? [];
        const allCounts = this.eventStore.attendanceCounts();
        const counts = states.map((state: AttendanceState) => ({
            name: attendanceDisplayName[state],
            count: allCounts[state] ?? 0,
        }));
        return { ...settings, states: new Set(states), counts };
    });

    @Bind
    async onRegisterClick() {
        if (!this.attendanceStore.canRegister()) return;

        if (!this.loginStore.loggedIn()) {
            return this.registerAsGuest();
        }

        // If there is no form to fill out, let user register immediately (after selecting occurrences)
        if (!this.attendanceStore.registrationFormId()) {
            return this.eventStore.registerForOccurrences();
        }

        this.eventStore.setShowRegistrationForm(true);
    }

    private async registerAsGuest() {
        const response = await ModalService.modal("se-guest-registration-request", {
            injector: this.injector,
        });
        if (!response) return;

        if (response.messageCode === "registration_email_sent") {
            NotificationService.post("Check your email for a link to complete registration", 10 * S25Const.ms.sec);
        }
    }

    @Bind
    async onUnregisterClick() {
        return this.eventStore.unregisterForOccurrences();
    }

    async onRegistered(data: { submissionId: number; answers: IngestionFieldAnswer[] }) {
        this.eventStore.setShowRegistrationForm(false);
        // Get occurrences
        const occs = data.answers
            .filter((answer) => answer.fieldDefinitionId === INGESTION_SYSTEM_FIELDS.MULTISELECT_OCCURRENCES)
            .map((answer) => Number(answer.answerRaw));

        await this.attendanceStore.setAttendance("PENDING", { submissionId: data.submissionId, occurrences: occs });
    }

    @Bind
    async setInterested(reservationId?: number) {
        const options: UpdateAttendanceOptions = { occurrences: [] };
        if (reservationId) options.occurrences!.push(reservationId);
        await this.attendanceStore.setAttendance("INTERESTED", options);
    }

    async setAtLeastInterested() {
        const occurrences = this.eventStore
            .occurrences()
            ?.filter((occ) => !occ.userAttendance)
            .map((occ) => occ.reservationId);
        await this.attendanceStore.setAttendance("INTERESTED", { occurrences });
    }

    @Bind
    async setNotInterested() {
        await this.attendanceStore.setAttendance("NOT_INTERESTED");
    }

    asIconType(icon: string): S25Icons.Name {
        return <S25Icons.Name>icon;
    }
}

type FieldOption = "visible" | "icon" | "label" | "css";
type FieldSettings<ID extends string> = {
    [K in FieldOption]: Merge<Layouts.WidgetStaticSetting, { id: `${ID}-${K}` }>;
}[FieldOption];

function defaultFields<ID extends string>(
    id: ID,
    label: string,
    info: string | undefined,
    fieldConfig: Partial<Record<FieldOption, unknown>>,
) {
    const availableSettings = {
        visible: { id: `${id}-visible`, label: "Visible", type: "field", fieldType: "checkbox" },
        icon: { id: `${id}-icon`, label: "Icon", type: "field", fieldType: "icon" },
        label: { id: `${id}-label`, label: "Label", type: "field", fieldType: "text" },
        css: {
            id: `${id}-css`,
            label: "Styles",
            type: "field",
            fieldType: "css",
            options: { cssProps: LayoutsConfig.allowedCssProperties },
        },
    } as const satisfies Record<FieldOption, FieldSettings<ID>>;

    return {
        id,
        label,
        info,
        type: "fields" as const,
        settings: S25Util.objectKeys(availableSettings)
            .filter((key) => fieldConfig[key] !== false)
            .map((key) => availableSettings[key as FieldOption]),
    };
}
